import { useLayoutEffect, useRef, useState } from "preact/hooks";

export function useAnchoredFloatLayout({
  anchorRef,
  isOpen,
  preferredHeight,
  minHeight = 96,
  offset = 1,
  placement = "bottom-start",
  matchAnchorWidth = true,
  onAnchorInvalidated,
  getScrollContainer
}) {
  const [layout, setLayout] = useState(null);
  const documentDirection = document.documentElement.dir || "ltr";
  const lastDirectionRef = useRef(documentDirection);

  useLayoutEffect(() => {
    const anchorElement = anchorRef.current;

    if (!isOpen || !anchorElement) {
      setLayout(null);
      lastDirectionRef.current = documentDirection;
      return;
    }

    if (lastDirectionRef.current !== documentDirection) {
      lastDirectionRef.current = documentDirection;
      onAnchorInvalidated?.();
      setLayout(null);
      return;
    }

    const scrollContainer = getScrollContainer?.(anchorElement) ?? null;
    const viewportMargin = 8;

    function updateLayout() {
      const anchorRect = anchorElement.getBoundingClientRect();
      const direction = window.getComputedStyle(anchorElement).direction || document.documentElement.dir || "ltr";
      const inlinePlacement = placement === "inline-end";

      if (inlinePlacement) {
        const preferredTop = anchorRect.top + (anchorRect.height / 2);
        const boundedTop = Math.max(
          viewportMargin,
          Math.min(preferredTop, window.innerHeight - viewportMargin)
        );

        setLayout({
          direction,
          left: direction === "rtl" ? null : (anchorRect.right + offset),
          right: direction === "rtl" ? (window.innerWidth - anchorRect.left + offset) : null,
          width: matchAnchorWidth ? anchorRect.width : null,
          maxHeight: Math.max(window.innerHeight - (viewportMargin * 2), minHeight),
          top: boundedTop,
          bottom: null,
          openUpward: false,
          placement
        });
        return;
      }

      const spaceBelow = window.innerHeight - anchorRect.bottom - viewportMargin;
      const spaceAbove = anchorRect.top - viewportMargin;
      const openUpward = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
      const availableHeight = Math.max(openUpward ? spaceAbove : spaceBelow, minHeight);

      setLayout({
        direction,
        left: direction === "rtl" ? null : anchorRect.left,
        right: direction === "rtl" ? (window.innerWidth - anchorRect.right) : null,
        width: matchAnchorWidth ? anchorRect.width : null,
        maxHeight: availableHeight,
        top: openUpward ? null : (anchorRect.bottom + offset),
        bottom: openUpward ? (window.innerHeight - anchorRect.top + offset) : null,
        openUpward,
        placement
      });
    }

    updateLayout();
    lastDirectionRef.current = documentDirection;

    const initialRect = anchorElement.getBoundingClientRect();
    const initialViewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    const handleViewportChange = () => {
      const nextRect = anchorElement.getBoundingClientRect();
      const viewportChanged = window.innerWidth !== initialViewport.width
        || window.innerHeight !== initialViewport.height;
      const anchorMoved = Math.abs(nextRect.top - initialRect.top) > 1
        || Math.abs(nextRect.left - initialRect.left) > 1
        || Math.abs(nextRect.width - initialRect.width) > 1
        || Math.abs(nextRect.height - initialRect.height) > 1;

      if (viewportChanged || anchorMoved) {
        onAnchorInvalidated?.();
      }
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    scrollContainer?.addEventListener("scroll", handleViewportChange);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      scrollContainer?.removeEventListener("scroll", handleViewportChange);
    };
  }, [
    anchorRef,
    getScrollContainer,
    isOpen,
    minHeight,
    placement,
    matchAnchorWidth,
    offset,
    onAnchorInvalidated,
    documentDirection,
    preferredHeight
  ]);

  return layout;
}
